
import React from "react";
import { Box, Grid, Typography, Paper } from "@mui/material";
import { AccountBalanceOutlined, LocalShipping, LocalShippingOutlined, SwapHorizOutlined } from "@mui/icons-material";
import { twMerge } from "tailwind-merge";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import ShipmentLineChart from "./ShipmentVendorLineChart";
import { useNavigate } from "react-router-dom";
import PaymentLineChart from "./VendorPaymentLineChart";

export const VendorAnalyticOverview = ({ statsData, isLoading }) => {
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const stats = [
    {
      title: "Shipments",
      icon: <LocalShippingOutlined fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Total Shipments",
          count: statsData?.totalShipments ?? 0,
          link: "/app/vendor/shipment/list"
        },
        {
          label: "Collected Shipments",
          count: statsData?.collectedShipments ?? 0,
          link: "/app/vendor/shipment/list?tab=Collected"
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
    {
      title: "Delivery",
      icon: <LocalShipping fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Delivered Shipments",
          count: statsData?.deliveredShipments ?? 0,
          link: "/app/vendor/shipment/list?tab=Delivered"
        },
        {
          label: "Returned Shipments",
          count: statsData?.returningShipments ?? 0,
          link: "/app/vendor/shipment/list?shipmentStatus=IsReturning"
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
    {
      title: "Counter Collection",
      icon: <AccountBalanceOutlined fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Total Collection",
          count: statsData?.totalCollectionAmount ? `Rs. ${statsData.totalCollectionAmount}` : "Rs. 0",
        },
        statsData?.recentCollection ? {
          label: "Latest Collection",
          count: (
            <Typography variant="body2" noWrap>
              {statsData.recentCollection.cocCollectionId} •
              Rs. {statsData.recentCollection.collectionAmount} •
              {formatDate(statsData.recentCollection.collectionDate)}
            </Typography>
          ),
          link: "/app/vendor/counter-collection/list"
        } : {
          label: "No Collections",
          count: "N/A",
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
    {
      title: "COD Transfer",
      icon: <SwapHorizOutlined fontSize="large" className="text-blue-500" />,
      items: [
        {
          label: "Total Transfer",
          count: statsData?.totalTransferAmount ? `Rs. ${statsData.totalTransferAmount}` : "Rs. 0",
        },
        statsData?.recentTransfer ? {
          label: "Latest Transfer",
          count: (
            <Typography variant="body2" noWrap>
              {statsData.recentTransfer.codTransferId} •
              Rs. {statsData.recentTransfer.transferAmount} •
              {formatDate(statsData.recentTransfer.transferDate)}
            </Typography>
          ),
          link: "/app/vendor/cod-transfer/list"
        } : {
          label: "No Transfers",
          count: "N/A",
        },
      ],
      bgColor: "bg-blue-50 dark:bg-blue-900",
    },
  ];

  if (isLoading) return <InfoGridSkeleton />;

  return (
    <Box>
      <Grid container spacing={2}>
        {stats.map((stat) => (
          <Grid
            key={stat.title}
            item
            xs={12}
            sm={6}
            md={3}
            sx={{
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              "&:hover": { transform: "scale(1.05)" }
            }}
          >
            <Paper
              className={twMerge("overflow-hidden shadow-md transition-all duration-300 p-3", stat.bgColor)}
              sx={{ borderRadius: 4, height: '100%' }}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" fontWeight="bold" className="text-gray-900 dark:text-white">
                  {stat.title}
                </Typography>
                {stat.icon}
              </Box>
              <Box>
                {stat.items.map((item, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    p={0.5}
                    className="hover:bg-opacity-50 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-1000"
                    onClick={() => navigate(item.link)}
                  >
                    <Typography variant="subtitle1" color="textSecondary" noWrap>
                      {item.label}
                    </Typography>
                    {typeof item.count === 'string' || typeof item.count === 'number' ? (
                      <Typography variant="body1" fontWeight="bold" className="text-gray-900 dark:text-white" noWrap>
                        {item.count}
                      </Typography>
                    ) : (
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0, ml: 1 }}>
                        {item.count}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={6}>
          <ShipmentLineChart />
        </Grid>
        <Grid item xs={12} md={6}>
          <PaymentLineChart />
        </Grid>
      </Grid>
    </Box>
  );
};